// components/POSShippingModal.jsx
import React, { useState, useEffect } from "react";
import { updatePOSOrderShipping, invokePOSShippedEmail, loadShippingCarrier } from "../services/posService";
import ShippingLabelModal from "./ShippingLabelModal";
import "./POSShippingModal.css";

const STATUSES = ["Pending", "Ordered from Vendor", "Shipped", "Delivered"];
const CARRIERS = [
  "",
  "FedEx",
  "UPS",
  "USPS",
  "XPO Logistics",
  "ArcBest",
  "Southeastern Freight Lines",
  "Echo Logistics",
  "AAA Cooper Transportation",
];

export default function POSShippingModal({ order, onClose, onSaved, user, onNotifyResult }) {
  const [form, setForm] = useState({
    status:            order.status            || "Pending",
    shipDate:          order.shipDate          || "",
    estDelivery:       order.estDelivery       || "",
    carrier:           order.carrier           || "",
    tracking:          order.tracking          || "",
    // Default to whatever was set when the order was created. The normalizer
    // in posService.js coerces the column to true when null, so a missing
    // value is treated as "yes, notify".
    notifyDistributor: order.notifyDistributor !== false,
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);
  const [labelEnabled, setLabelEnabled] = useState(false);
  const [showLabel, setShowLabel] = useState(false);

  useEffect(() => {
    loadShippingCarrier("ups")
      .then(c => setLabelEnabled(!!(c?.enabled && c?.has_credentials)))
      .catch(() => {});
  }, []);

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  // A label was created server-side (carrier/tracking/ship_date already stamped
  // on the order). Reflect it in this form and bubble up to the grid.
  const handleLabeled = (fields) => {
    setForm(prev => ({ ...prev, ...fields }));
    onSaved({ id: order.id, ...fields });
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const wasShipped = order.status === "Shipped";
      await updatePOSOrderShipping(order.id, form);
      // Notify on the transition to Shipped. This fires AFTER the save resolves
      // because pos-shipped-notify re-reads the row — including the toggled
      // notifyDistributor — rather than trusting anything we pass it.
      // Item 204: the result is reported up so the page can say the notice did
      // not go out — it used to resolve `{sent:false}` straight into a no-op.
      if (form.status === "Shipped" && !wasShipped) {
        invokePOSShippedEmail({ ...order, ...form }, form, user?.mail)
          .then(r => onNotifyResult?.(r))
          .catch(err => { console.error(err); onNotifyResult?.({ sent: false, reason: "invoke_failed" }); });
      }
      onSaved({ id: order.id, ...form });
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <div className="psm-backdrop" onClick={onClose}>
      <div className="psm-modal" onClick={e => e.stopPropagation()}>
        <div className="psm-header">
          <div>
            <div className="psm-title">Edit Shipping — {order.orderNumber}</div>
            <div className="psm-subtitle">{order.shipToCompany}</div>
          </div>
          <button className="psm-close" onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div className="psm-body">
          {error && <div className="psm-error">{error}</div>}

          <div className="psm-row">
            <div className="psm-field psm-grow">
              <label>Status</label>
              <select value={form.status} onChange={e => set("status", e.target.value)}>
                {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          <div className="psm-row">
            <div className="psm-field psm-grow">
              <label>Ship Date</label>
              <input type="date" value={form.shipDate} onChange={e => set("shipDate", e.target.value)} />
            </div>
            <div className="psm-field psm-grow">
              <label>Est. Delivery</label>
              <input type="date" value={form.estDelivery} onChange={e => set("estDelivery", e.target.value)} />
            </div>
          </div>

          <div className="psm-row">
            <div className="psm-field psm-grow">
              <label>Carrier</label>
              <select value={form.carrier} onChange={e => set("carrier", e.target.value)}>
                {CARRIERS.map(c => <option key={c} value={c}>{c || "— Select —"}</option>)}
              </select>
            </div>
            <div className="psm-field psm-grow">
              <label>Tracking Number</label>
              <input value={form.tracking} onChange={e => set("tracking", e.target.value)} placeholder="Tracking #" />
            </div>
          </div>

          <div className="psm-row">
            <div className="psm-field psm-grow">
              <label className="psm-check-label">
                <input
                  type="checkbox"
                  checked={form.notifyDistributor}
                  onChange={e => set("notifyDistributor", e.target.checked)}
                />
                Send Distributor Ship To Contact Tracking Info
              </label>
              <div className="psm-check-note">
                When checked, the ship-to contact gets a tracking email the moment this order moves to <strong>Shipped</strong>.
              </div>
            </div>
          </div>
        </div>

        <div className="psm-footer">
          <button className="psm-cancel" onClick={onClose} disabled={saving}>Cancel</button>
          {labelEnabled && (
            <button
              type="button"
              className="psm-secondary"
              onClick={() => setShowLabel(true)}
            >
              Create UPS Label
            </button>
          )}
          <button className="psm-save"   onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>

      {showLabel && (
        <ShippingLabelModal
          order={order}
          orderType="pos"
          onClose={() => setShowLabel(false)}
          onLabeled={handleLabeled}
        />
      )}
    </div>
  );
}
